<script setup lang="ts">
import type { Property } from '@/modules/Project/Properties/types'
import ConfirmationDialog from '@/uiKit/ConfirmationDialog.vue'
import IconSprite from '@/uiKit/IconSprite.vue'
import Select from '@/uiKit/Select/Select.vue'
import { ref, watch } from 'vue'

/**
 * Renders a modal dialog that allows the user to select a file property to upload
 * to when they drop files into the project table dropzone.
 */

const props = defineProps<{
  open: boolean
  files: File[]
  properties: Property[]
}>()

const emit = defineEmits<{
  cancel: []
  confirm: [propertyId: string]
}>()

const selectedPropertyId = ref(props.properties[0]?.id)
watch(
  () => props.open,
  () => {
    selectedPropertyId.value = props.properties[0]?.id
  },
)

const onConfirm = () => {
  if (!selectedPropertyId.value) {
    throw new Error('No property selected - cannot confirm')
  }

  emit('confirm', selectedPropertyId.value)
}
</script>

<template>
  <ConfirmationDialog
    class="min-w-96"
    title="Choose file property"
    description="Choose a file property to upload these files to"
    confirm-text="Confirm"
    variant="black"
    :open="open"
    :disable-actions="!selectedPropertyId"
    @close="$emit('cancel')"
    @confirm="onConfirm"
  >
    <div class="flex flex-col">
      <div class="mb-2 text-text">Uploading {{ files.length }} files:</div>
      <ul class="go-scrollbar mb-4 max-h-64 overflow-y-auto">
        <li
          v-for="file in files"
          :key="`${file.name}:${file.size}`"
          class="mb-1 flex items-center gap-1"
        >
          <IconSprite
            icon="file"
            size="sm"
          />
          <div>
            {{ file.name }}
          </div>
        </li>
      </ul>
      <div class="mb-2">Select a file property to upload to:</div>
      <Select
        :items="
          properties.map((p) => ({
            label: p.name,
            value: p.id,
          }))
        "
        :value="selectedPropertyId"
        disable-teleport
        :positioning="{
          offset: {
            mainAxis: 4,
            crossAxis: 0,
          },
        }"
        @change="selectedPropertyId = $event"
      />
    </div>
  </ConfirmationDialog>
</template>
